import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import api from '../functions/api'; // DAS-3127: sends the login token (raw axios got 401)

const SectorSummaryDashboard = ({ open, onClose }) => {
  const [sectorData, setSectorData] = useState([]);
  const [sectorIndustryData, setSectorIndustryData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  
  // Modal state for company details
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [modalTitle, setModalTitle] = useState('');

  useEffect(() => {
    if (open) {
      fetchSectorSummary();
    }
  }, [open]);

  const fetchSectorSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch sector-level, sector/industry-level, and all data in parallel
      const [sectorResponse, sectorIndustryResponse, allDataResponse] = await Promise.all([
        api.get('/trendsearch_sector_summary'),
        api.get('/trendsearch_sector_industry_summary'),
        api.get('/trendsearch_all_data')
      ]);
      
      if (sectorResponse.data.success) {
        setSectorData(sectorResponse.data.data);
        setGeneratedAt(sectorResponse.data.generated_at);
      } else {
        setError('Failed to fetch sector summary data');
        return;
      }
      
      if (sectorIndustryResponse.data.success) {
        setSectorIndustryData(sectorIndustryResponse.data.data);
      } else {
        setError('Failed to fetch sector/industry summary data');
      }
      
      if (allDataResponse.data.success) {
        setAllData(allDataResponse.data.data);
      } else {
        setError('Failed to fetch all data');
      }
      
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Function to handle clicking on W/L/E counts
  const handleCountClick = (sector, impactType, industry = null) => {
    const filteredCompanies = allData.filter(item => {
      const sectorMatch = item.sector === sector;
      const impactMatch = item.impact_type === impactType;
      const industryMatch = industry ? item.industry === industry : true;
      return sectorMatch && impactMatch && industryMatch;
    });

    setSelectedCompanies(filteredCompanies);
    
    const impactTypeLabel = impactType === 'company_winner' ? 'Winners' : 
                           impactType === 'company_loser' ? 'Losers' : 'Enablers';
    const title = industry ? 
      `${impactTypeLabel} in ${sector} / ${industry}` : 
      `${impactTypeLabel} in ${sector}`;
    setModalTitle(title);
    setCompanyModalOpen(true);
  };

  // Function to handle clicking on Top Opportunities
  const handleOpportunitiesClick = (sector, industry = null) => {
    const filteredCompanies = allData.filter(item => {
      const sectorMatch = item.sector === sector;
      const impactMatch = item.impact_type === 'company_winner';
      const sentimentMatch = item.final_sentiment_score >= 7;
      const industryMatch = industry ? item.industry === industry : true;
      return sectorMatch && impactMatch && sentimentMatch && industryMatch;
    });

    setSelectedCompanies(filteredCompanies);
    
    const title = industry ? 
      `Top Opportunities in ${sector} / ${industry}` : 
      `Top Opportunities in ${sector}`;
    setModalTitle(title);
    setCompanyModalOpen(true);
  };

  // Function to handle clicking on Top Risks
  const handleRisksClick = (sector, industry = null) => {
    const filteredCompanies = allData.filter(item => {
      const sectorMatch = item.sector === sector;
      const impactMatch = item.impact_type === 'company_loser';
      const sentimentMatch = item.final_sentiment_score <= -5;
      const industryMatch = industry ? item.industry === industry : true;
      return sectorMatch && impactMatch && sentimentMatch && industryMatch;
    });

    setSelectedCompanies(filteredCompanies);
    
    const title = industry ? 
      `Top Risks in ${sector} / ${industry}` : 
      `Top Risks in ${sector}`;
    setModalTitle(title);
    setCompanyModalOpen(true);
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment === null || sentiment === undefined) return '#9e9e9e';
    if (sentiment >= 7) return '#4caf50';
    if (sentiment >= 3) return '#8bc34a';
    if (sentiment >= -3) return '#ffeb3b';
    if (sentiment >= -7) return '#ff9800';
    return '#f44336';
  };

  const formatTrendHorizon = (horizonText) => {
    const parts = horizonText.split(' | ');
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {parts.map((part, index) => (
          <Typography key={index} variant="caption" sx={{ fontSize: '10px' }}>
            {part}
          </Typography>
        ))}
      </Box>
    );
  };

  const formatOpportunities = (opportunities) => {
    if (opportunities === 'N/A') return 'N/A';
    const items = opportunities.split(', ').slice(0, 3); // Show top 3
    return items.join(', ') + (opportunities.split(', ').length > 3 ? '...' : '');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '96vw',
          height: '90vh',
          maxWidth: '96vw',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Sector Health Check</Typography>
          <Typography variant="caption" color="text.secondary">Aggregated trend analysis by sector and industry</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {generatedAt && (
            <Typography variant="caption" color="text.secondary">
              Generated: {generatedAt}
            </Typography>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: 0, height: 'calc(100% - 64px)', display: 'flex', flexDirection: 'column' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ padding: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {!loading && !error && (sectorData.length > 0 || sectorIndustryData.length > 0) && (
          <>
            {/* Tab Navigation */}
            <Box data-testid="sector-health-loaded" sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: '40px' }}>
                <Tab 
                  label={`Sector Overview (${sectorData.length})`}
                  sx={{ fontSize: '12px', minHeight: '40px', padding: '8px 16px' }}
                />
                <Tab 
                  label={`Sector/Industry Detail (${sectorIndustryData.length})`}
                  sx={{ fontSize: '12px', minHeight: '40px', padding: '8px 16px' }}
                />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              {activeTab === 0 && sectorData.length > 0 && (
          <TableContainer 
            component={Paper} 
            sx={{ 
              height: '100%',
              '& .MuiTableCell-root': {
                padding: '6px 10px',
                fontSize: '12px',
                borderRight: '1px solid #e0e0e0'
              }
            }}
          >
            <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <Tooltip title="Financial sector classification (Technology, Healthcare, etc.)" placement="top"><TableCell sx={{ width: '140px', fontWeight: 'bold', backgroundColor: '#fafafa', cursor: 'help' }}>Sector</TableCell></Tooltip>
                  <Tooltip title="Average sentiment of sector-level market changes only (excludes individual companies). Scale: -10 (very negative) to +10 (very positive). Shows how many trend records contributed to this average." placement="top">
                    <TableCell sx={{ width: '90px', fontWeight: 'bold', backgroundColor: '#fafafa', cursor: 'help' }}>
                      Overall Sentiment
                    </TableCell>
                  </Tooltip>
                  <Tooltip title="Number of sector-level market changes identified (regulatory shifts, market expansions, industry disruptions, etc.)" placement="top">
                    <TableCell sx={{ width: '70px', fontWeight: 'bold', backgroundColor: '#fafafa', cursor: 'help' }}>
                      Trend Count
                    </TableCell>
                  </Tooltip>
                  <Tooltip title="Top financial metrics mentioned in sector trends (valuations, funding amounts, market sizes, growth rates)" placement="top">
                    <TableCell sx={{ width: '180px', fontWeight: 'bold', backgroundColor: '#fafafa', cursor: 'help' }}>
                      Key Metrics
                    </TableCell>
                  </Tooltip>
                  <Tooltip title="Companies identified as winners in this sector with sentiment score ≥7.0 (benefiting from sector trends)" placement="top">
                    <TableCell sx={{ width: '220px', fontWeight: 'bold', backgroundColor: '#fafafa', cursor: 'help' }}>
                      Top Opportunities
                    </TableCell>
                  </Tooltip>
                  <Tooltip title="Companies identified as losers in this sector with sentiment score ≤-5.0 (negatively impacted by sector trends)" placement="top">
                    <TableCell sx={{ width: '220px', fontWeight: 'bold', backgroundColor: '#fafafa', cursor: 'help' }}>
                      Top Risks
                    </TableCell>
                  </Tooltip>
                  <Tooltip title="Timeline distribution of sector trends: Immediate (≤3 months), Medium-Term (4-12 months), Long-Term (>12 months)" placement="top">
                    <TableCell sx={{ width: '160px', fontWeight: 'bold', backgroundColor: '#fafafa', cursor: 'help' }}>
                      Trend Horizon
                    </TableCell>
                  </Tooltip>
                  <Tooltip title="Count of companies in this sector: Winners (benefiting), Losers (harmed), Enablers (facilitating changes)" placement="top">
                    <TableCell sx={{ width: '110px', fontWeight: 'bold', backgroundColor: '#fafafa', cursor: 'help' }}>
                      W/L/E Count
                    </TableCell>
                  </Tooltip>
                  <Tooltip title="Number of new impacts (all types) identified in this sector within the last 7 days" placement="top">
                    <TableCell sx={{ width: '90px', fontWeight: 'bold', backgroundColor: '#fafafa', cursor: 'help' }}>
                      Recent Activity
                    </TableCell>
                  </Tooltip>
                </TableRow>
              </TableHead>
              <TableBody>
                {sectorData.map((sector, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={`Sentiment indicator: ${sector.sentiment_status} (${sector.overall_sentiment || 'N/A'})`} placement="right">
                          <span style={{ cursor: 'help' }}>{sector.sentiment_indicator}</span>
                        </Tooltip>
                        <Chip label={sector.sector} size="small" variant="outlined" color="secondary" />
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Chip
                          label={sector.overall_sentiment || 'N/A'}
                          size="small"
                          sx={{
                            backgroundColor: getSentimentColor(sector.overall_sentiment),
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '12px',
                            height: '22px'
                          }}
                        />
                        <Typography variant="caption" sx={{ fontSize: '11px', marginTop: '2px' }}>
                          {sector.sentiment_status}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '10px', color: '#666', fontStyle: 'italic' }}>
                          ({sector.sentiment_coverage} records)
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <TrendingUpIcon sx={{ fontSize: '14px', color: '#1976d2' }} />
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {sector.trend_count}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Tooltip title={sector.key_metrics} placement="top">
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: '12px',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {sector.key_metrics}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    
                    <TableCell>
                      <Tooltip title={`Click to view top opportunities: ${sector.top_opportunities}`} placement="top">
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: '12px',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: '#4caf50',
                            cursor: sector.top_opportunities && sector.top_opportunities !== 'N/A' ? 'pointer' : 'help',
                            textDecoration: sector.top_opportunities && sector.top_opportunities !== 'N/A' ? 'underline' : 'none',
                            '&:hover': sector.top_opportunities && sector.top_opportunities !== 'N/A' ? { color: '#2e7d32' } : {}
                          }}
                          onClick={() => sector.top_opportunities && sector.top_opportunities !== 'N/A' && handleOpportunitiesClick(sector.sector)}
                        >
                          {formatOpportunities(sector.top_opportunities)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    
                    <TableCell>
                      <Tooltip title={`Click to view top risks: ${sector.top_risks}`} placement="top">
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: '12px',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: '#f44336',
                            cursor: sector.top_risks && sector.top_risks !== 'N/A' ? 'pointer' : 'help',
                            textDecoration: sector.top_risks && sector.top_risks !== 'N/A' ? 'underline' : 'none',
                            '&:hover': sector.top_risks && sector.top_risks !== 'N/A' ? { color: '#c62828' } : {}
                          }}
                          onClick={() => sector.top_risks && sector.top_risks !== 'N/A' && handleRisksClick(sector.sector)}
                        >
                          {sector.top_risks}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    
                    <TableCell>
                      {formatTrendHorizon(sector.trend_horizon)}
                    </TableCell>
                    
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                        <Tooltip title="Click to view winner companies: Benefiting from sector trends" placement="left">
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: '12px', 
                              color: '#4caf50', 
                              cursor: sector.winner_count > 0 ? 'pointer' : 'help',
                              textDecoration: sector.winner_count > 0 ? 'underline' : 'none',
                              '&:hover': sector.winner_count > 0 ? { color: '#2e7d32' } : {}
                            }}
                            onClick={() => sector.winner_count > 0 && handleCountClick(sector.sector, 'company_winner')}
                          >
                            W: {sector.winner_count}
                          </Typography>
                        </Tooltip>
                        <Tooltip title="Click to view loser companies: Negatively impacted by sector trends" placement="left">
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: '12px', 
                              color: '#f44336', 
                              cursor: sector.loser_count > 0 ? 'pointer' : 'help',
                              textDecoration: sector.loser_count > 0 ? 'underline' : 'none',
                              '&:hover': sector.loser_count > 0 ? { color: '#c62828' } : {}
                            }}
                            onClick={() => sector.loser_count > 0 && handleCountClick(sector.sector, 'company_loser')}
                          >
                            L: {sector.loser_count}
                          </Typography>
                        </Tooltip>
                        <Tooltip title="Click to view enabler companies: Facilitating or supporting sector changes" placement="left">
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: '12px', 
                              color: '#ff9800', 
                              cursor: sector.enabler_count > 0 ? 'pointer' : 'help',
                              textDecoration: sector.enabler_count > 0 ? 'underline' : 'none',
                              '&:hover': sector.enabler_count > 0 ? { color: '#ef6c00' } : {}
                            }}
                            onClick={() => sector.enabler_count > 0 && handleCountClick(sector.sector, 'company_enabler')}
                          >
                            E: {sector.enabler_count}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                        <Tooltip title={`${sector.recent_activity} new impacts (market changes + company impacts) identified in this sector over the last 7 days`} placement="left">
                          <Chip
                            label={sector.recent_activity}
                            size="small"
                            color={sector.recent_activity > 0 ? "success" : "default"}
                            sx={{ fontSize: '12px', height: '22px', cursor: 'help' }}
                          />
                        </Tooltip>
                        <Typography variant="caption" sx={{ fontSize: '10px' }}>7 days</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
              )}

              {activeTab === 1 && sectorIndustryData.length > 0 && (
                <TableContainer 
                  component={Paper} 
                  sx={{ 
                    height: '100%',
                    '& .MuiTableCell-root': {
                      padding: '4px 8px',
                      fontSize: '12px',
                      borderRight: '1px solid #e0e0e0'
                    }
                  }}
                >
                  <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow>
                        <Tooltip title="Sector and specific industry classification (Technology / Software - Application)" placement="top">
                          <TableCell sx={{ width: '180px', fontWeight: 'bold', backgroundColor: '#f5f5f5', cursor: 'help' }}>
                            Sector / Industry
                          </TableCell>
                        </Tooltip>
                        <Tooltip title="Average sentiment of industry-level market changes. Scale: -10 (very negative) to +10 (very positive). Shows coverage transparency." placement="top">
                          <TableCell sx={{ width: '80px', fontWeight: 'bold', backgroundColor: '#f5f5f5', cursor: 'help' }}>
                            Sentiment
                          </TableCell>
                        </Tooltip>
                        <Tooltip title="Number of industry-specific market changes identified" placement="top">
                          <TableCell sx={{ width: '60px', fontWeight: 'bold', backgroundColor: '#f5f5f5', cursor: 'help' }}>
                            Trends
                          </TableCell>
                        </Tooltip>
                        <Tooltip title="Top financial metrics for this specific industry" placement="top">
                          <TableCell sx={{ width: '140px', fontWeight: 'bold', backgroundColor: '#f5f5f5', cursor: 'help' }}>
                            Key Metrics
                          </TableCell>
                        </Tooltip>
                        <Tooltip title="Top opportunities in this specific industry (sentiment ≥7.0)" placement="top">
                          <TableCell sx={{ width: '180px', fontWeight: 'bold', backgroundColor: '#f5f5f5', cursor: 'help' }}>
                            Top Opportunities
                          </TableCell>
                        </Tooltip>
                        <Tooltip title="Top risks in this specific industry (sentiment ≤-5.0)" placement="top">
                          <TableCell sx={{ width: '180px', fontWeight: 'bold', backgroundColor: '#f5f5f5', cursor: 'help' }}>
                            Top Risks
                          </TableCell>
                        </Tooltip>
                        <Tooltip title="Timeline distribution for this industry's trends" placement="top">
                          <TableCell sx={{ width: '120px', fontWeight: 'bold', backgroundColor: '#f5f5f5', cursor: 'help' }}>
                            Horizon
                          </TableCell>
                        </Tooltip>
                        <Tooltip title="Company impact counts for this industry" placement="top">
                          <TableCell sx={{ width: '80px', fontWeight: 'bold', backgroundColor: '#f5f5f5', cursor: 'help' }}>
                            W/L/E
                          </TableCell>
                        </Tooltip>
                        <Tooltip title="Recent activity in this specific industry (last 7 days)" placement="top">
                          <TableCell sx={{ width: '70px', fontWeight: 'bold', backgroundColor: '#f5f5f5', cursor: 'help' }}>
                            Activity
                          </TableCell>
                        </Tooltip>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sectorIndustryData.map((item, index) => (
                        <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title={`Industry sentiment: ${item.sentiment_status} (${item.overall_sentiment || 'N/A'})`} placement="right">
                                  <span style={{ cursor: 'help' }}>{item.sentiment_indicator}</span>
                                </Tooltip>
                                <Typography variant="caption" sx={{ fontSize: '10px', fontWeight: 'bold' }}>
                                  {item.sector}
                                </Typography>
                              </Box>
                              <Typography variant="caption" sx={{ fontSize: '9px', color: '#666', fontStyle: 'italic' }}>
                                {item.industry}
                              </Typography>
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Chip
                                label={item.overall_sentiment || 'N/A'}
                                size="small"
                                sx={{
                                  backgroundColor: getSentimentColor(item.overall_sentiment),
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: '9px',
                                  height: '18px'
                                }}
                              />
                              <Typography variant="caption" sx={{ fontSize: '8px', color: '#666', fontStyle: 'italic' }}>
                                ({item.sentiment_coverage})
                              </Typography>
                            </Box>
                          </TableCell>
                          
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '11px' }}>
                              {item.trend_count}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Tooltip title={item.key_metrics} placement="top">
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: '9px',
                                  display: 'block',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                              >
                                {item.key_metrics}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          
                          <TableCell>
                            <Tooltip title={`Click to view top opportunities: ${item.top_opportunities}`} placement="top">
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: '9px',
                                  display: 'block',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  color: '#4caf50',
                                  cursor: item.top_opportunities && item.top_opportunities !== 'N/A' ? 'pointer' : 'help',
                                  textDecoration: item.top_opportunities && item.top_opportunities !== 'N/A' ? 'underline' : 'none',
                                  '&:hover': item.top_opportunities && item.top_opportunities !== 'N/A' ? { color: '#2e7d32' } : {}
                                }}
                                onClick={() => item.top_opportunities && item.top_opportunities !== 'N/A' && handleOpportunitiesClick(item.sector, item.industry)}
                              >
                                {formatOpportunities(item.top_opportunities)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          
                          <TableCell>
                            <Tooltip title={`Click to view top risks: ${item.top_risks}`} placement="top">
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: '9px',
                                  display: 'block',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  color: '#f44336',
                                  cursor: item.top_risks && item.top_risks !== 'N/A' ? 'pointer' : 'help',
                                  textDecoration: item.top_risks && item.top_risks !== 'N/A' ? 'underline' : 'none',
                                  '&:hover': item.top_risks && item.top_risks !== 'N/A' ? { color: '#c62828' } : {}
                                }}
                                onClick={() => item.top_risks && item.top_risks !== 'N/A' && handleRisksClick(item.sector, item.industry)}
                              >
                                {item.top_risks}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          
                          <TableCell>
                            {formatTrendHorizon(item.trend_horizon)}
                          </TableCell>
                          
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: '8px', 
                                  color: '#4caf50',
                                  cursor: item.winner_count > 0 ? 'pointer' : 'default',
                                  textDecoration: item.winner_count > 0 ? 'underline' : 'none',
                                  '&:hover': item.winner_count > 0 ? { color: '#2e7d32' } : {}
                                }}
                                onClick={() => item.winner_count > 0 && handleCountClick(item.sector, 'company_winner', item.industry)}
                              >
                                W:{item.winner_count}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: '8px', 
                                  color: '#f44336',
                                  cursor: item.loser_count > 0 ? 'pointer' : 'default',
                                  textDecoration: item.loser_count > 0 ? 'underline' : 'none',
                                  '&:hover': item.loser_count > 0 ? { color: '#c62828' } : {}
                                }}
                                onClick={() => item.loser_count > 0 && handleCountClick(item.sector, 'company_loser', item.industry)}
                              >
                                L:{item.loser_count}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: '8px', 
                                  color: '#ff9800',
                                  cursor: item.enabler_count > 0 ? 'pointer' : 'default',
                                  textDecoration: item.enabler_count > 0 ? 'underline' : 'none',
                                  '&:hover': item.enabler_count > 0 ? { color: '#ef6c00' } : {}
                                }}
                                onClick={() => item.enabler_count > 0 && handleCountClick(item.sector, 'company_enabler', item.industry)}
                              >
                                E:{item.enabler_count}
                              </Typography>
                            </Box>
                          </TableCell>
                          
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Chip
                              label={item.recent_activity}
                              size="small"
                              color={item.recent_activity > 0 ? "success" : "default"}
                              sx={{ fontSize: '8px', height: '16px' }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {/* Quick Reference Legend */}
            <Box sx={{ padding: 2, backgroundColor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', marginBottom: 1, display: 'block' }}>
                📖 Quick Reference:
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ fontSize: '10px' }}>
                    <strong>Sentiment:</strong> 🟢 Very Positive (≥7) | 🟡 Positive (3-6.9) | ⚪ Neutral (-2.9-2.9) | 🟠 Negative (-6.9-(-3)) | 🔴 Very Negative (≤-7)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ fontSize: '10px' }}>
                    <strong>Companies:</strong> <span style={{color: '#4caf50'}}>W</span> Winners | <span style={{color: '#f44336'}}>L</span> Losers | <span style={{color: '#ff9800'}}>E</span> Enablers
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ fontSize: '10px' }}>
                    <strong>Coverage:</strong> (X of Y records) = How many trends contributed to sentiment
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}

        {!loading && !error && sectorData.length === 0 && sectorIndustryData.length === 0 && (
          <Box sx={{ padding: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              No sector data available
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>

    {/* Company Details Modal */}
    <Dialog
      open={companyModalOpen}
      onClose={() => setCompanyModalOpen(false)}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{modalTitle}</Typography>
        <IconButton onClick={() => setCompanyModalOpen(false)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {selectedCompanies.length > 0 ? (
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Ticker</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Industry</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Impact</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Metric</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Timeline</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Sentiment</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedCompanies.map((company, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {company.company_name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {company.ticker ? (
                        <Chip 
                          label={company.ticker} 
                          size="small" 
                          color="primary"
                          sx={{ fontSize: '10px' }}
                        />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          Private
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {company.industry || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {company.impact_type === 'company_winner' && (
                          <>
                            <TrendingUpIcon sx={{ fontSize: '14px', color: '#4caf50' }} />
                            <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 'medium' }}>
                              Winner
                            </Typography>
                          </>
                        )}
                        {company.impact_type === 'company_loser' && (
                          <>
                            <TrendingDownIcon sx={{ fontSize: '14px', color: '#f44336' }} />
                            <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 'medium' }}>
                              Loser
                            </Typography>
                          </>
                        )}
                        {company.impact_type === 'company_enabler' && (
                          <>
                            <AssessmentIcon sx={{ fontSize: '14px', color: '#ff9800' }} />
                            <Typography variant="caption" sx={{ color: '#ff9800', fontWeight: 'medium' }}>
                              Enabler
                            </Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {company.financial_metric ? 
                          `$${(company.financial_metric / 1000000000).toFixed(1)}B` : 
                          'N/A'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {company.timeline_months ? 
                          `${company.timeline_months} months` : 
                          'N/A'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: company.final_sentiment_score >= 0 ? '#4caf50' : '#f44336',
                          fontWeight: 'medium'
                        }}
                      >
                        {company.final_sentiment_score ? 
                          company.final_sentiment_score.toFixed(1) : 
                          'N/A'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="caption" 
                        component="a"
                        href={company.article_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        {company.source_name}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ padding: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              No companies found
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default SectorSummaryDashboard;
